import { useState } from "react";
import { CHURCH, MEDIA } from "@/lib/church";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Phone, EnvelopeSimple, Clock, ArrowRight } from "@phosphor-icons/react";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            toast.success("Thank you! We'll be in touch soon.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="bg-white">
            <section className="relative min-h-[60vh] flex items-end overflow-hidden bg-[#4C1D95]">
                <div className="absolute inset-0">
                    <img src={MEDIA.family} alt="Community" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4C1D95]/95 via-[#4C1D95]/65 to-[#4C1D95]/20" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4C1D95]/80 via-transparent to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 w-full">
                    <p className="overline text-[#FFC72C] mb-4 animate-fade-up">Get In Touch</p>
                    <h1 className="font-display text-6xl sm:text-7xl lg:text-7xl font-bold tracking-tight text-white leading-[0.85] animate-fade-up animate-delay-100" data-testid="contact-title">
                        We'd love to<br />
                        <span className="text-[#FFC72C] italic">hear from you</span>.
                    </h1>
                    <p className="mt-8 text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed animate-fade-up animate-delay-200">
                        Whether you're planning your first visit, have a prayer request, or want to connect — drop us a note.
                    </p>
                </div>
            </section>

            <section className="bg-white py-20 lg:py-28" id="visit">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <p className="overline text-[#4C1D95] mb-4">Send a Message</p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2E1A47] leading-[0.9] mb-10">
                            Tell us about<br />your story.
                        </h2>
                        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest font-black text-[#2E1A47] mb-2">Name *</label>
                                    <input required value={form.name} onChange={update("name")} data-testid="contact-name-input" className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#4C1D95]" />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest font-black text-[#2E1A47] mb-2">Email *</label>
                                    <input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email-input" className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#4C1D95]" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest font-black text-[#2E1A47] mb-2">Phone</label>
                                    <input value={form.phone} onChange={update("phone")} data-testid="contact-phone-input" className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#4C1D95]" />
                                </div>
                                <div>
                                    <label className="block text-[10px] uppercase tracking-widest font-black text-[#2E1A47] mb-2">Subject</label>
                                    <input value={form.subject} onChange={update("subject")} data-testid="contact-subject-input" className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#4C1D95]" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest font-black text-[#2E1A47] mb-2">Message *</label>
                                <textarea required rows={6} value={form.message} onChange={update("message")} data-testid="contact-message-input" className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#4C1D95]" />
                            </div>
                            <button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="inline-flex items-center gap-2 bg-[#4C1D95] hover:bg-[#3B0F75] text-white px-10 py-4 text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50">
                                {submitting ? "Sending..." : "Send Message"} <ArrowRight size={14} weight="bold" />
                            </button>
                        </form>
                    </div>

                    <div className="lg:col-span-5 space-y-4">
                        <div className="bg-[#4C1D95] text-white p-8">
                            <p className="overline text-[#FFC72C] mb-4">Visit</p>
                            <p className="flex items-start gap-3 text-white/85 mb-3"><MapPin size={20} weight="bold" className="text-[#FFC72C] shrink-0 mt-0.5" /> {CHURCH.address}</p>
                            <a href={CHURCH.phoneHref} className="flex items-center gap-3 text-white/85 hover:text-white mb-3" data-testid="contact-page-phone"><Phone size={20} weight="bold" className="text-[#FFC72C]" /> {CHURCH.phone}</a>
                            <a href={CHURCH.emailHref} className="flex items-center gap-3 text-white/85 hover:text-white" data-testid="contact-page-email"><EnvelopeSimple size={20} weight="bold" className="text-[#FFC72C]" /> {CHURCH.email}</a>
                        </div>
                        <div className="bg-[#FFC72C] text-[#2E1A47] p-8">
                            <p className="overline mb-4">Service Times</p>
                            <ul className="space-y-3">
                                {CHURCH.services.map((s) => (
                                    <li key={s.label} className="flex items-center justify-between border-b border-[#0A192F]/15 pb-2 last:border-0">
                                        <span className="flex items-center gap-2 font-bold text-sm"><Clock size={14} weight="bold" /> {s.label}</span>
                                        <span className="font-black">{s.time}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="aspect-video w-full">
                            <iframe title="Map" src={CHURCH.mapEmbedUrl} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
